import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  X,
  ChevronRight,
  Search,
  Loader2,
  CheckCircle2,
  Workflow,
} from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";

interface WorkflowTemplate {
  id: string;
  steps: string[];
}

interface WorkflowAssignModalProps {
  open: boolean;
  layoutId: string;
  initialWorkflowId?: string;
  onClose: () => void;
  /** Called with the newly applied workflow_id after a successful assign. */
  onApplied?: (workflowId: string) => void;
}

/**
 * Mirrors ocr-ui's `workflow/EditWorkflow.jsx`: list available workflow
 * templates from `/management_layout/workflow_list`, let the user pick
 * one, and apply it to the current layout via assignWorkflow.
 */
function parseTemplates(raw: unknown): WorkflowTemplate[] {
  if (!raw || typeof raw !== "object") return [];
  // Backend wraps the list in `{ data: { ... } }`; sometimes the data
  // sits at the top level. Handle both.
  const obj = raw as Record<string, unknown>;
  const source =
    (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)
      ? (obj.data as Record<string, unknown>)
      : obj);
  return Object.entries(source).map(([id, value]) => {
    // Each value is typically a list-of-lists (legacy shape) or a flat
    // array of step names. Match the reference's mapping.
    let steps: string[] = [];
    if (Array.isArray(value) && Array.isArray(value[0])) {
      steps = (value[0] as unknown[]).map((s) => String(s));
    } else if (Array.isArray(value)) {
      steps = (value as unknown[]).map((s) => String(s));
    }
    return { id, steps };
  });
}

export function WorkflowAssignModal({
  open,
  layoutId,
  initialWorkflowId,
  onClose,
  onApplied,
}: WorkflowAssignModalProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(
    initialWorkflowId ?? "",
  );

  useEffect(() => {
    if (open) {
      setSelectedWorkflowId(initialWorkflowId ?? "");
      setSearch("");
    }
  }, [open, initialWorkflowId]);

  const workflowsQuery = useQuery({
    queryKey: ["ocr", "workflows", "list"],
    queryFn: () => ocrApi.listWorkflows(),
    enabled: open,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const templates = useMemo(
    () => parseTemplates(workflowsQuery.data),
    [workflowsQuery.data],
  );

  // Default the selection to the first template if none was provided.
  useEffect(() => {
    if (!selectedWorkflowId && templates.length > 0) {
      setSelectedWorkflowId(templates[0].id);
    }
  }, [templates, selectedWorkflowId]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => t.id.toLowerCase().includes(q));
  }, [templates, search]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedWorkflowId) ?? templates[0],
    [templates, selectedWorkflowId],
  );

  const assignMutation = useMutation({
    mutationFn: (workflowId: string) =>
      ocrApi.assignWorkflowToLayout({ layoutId, workflowId }),
    onSuccess: () => {
      toast.success(`Workflow "${selectedWorkflowId}" assigned to ${layoutId}.`);
      // Invalidate so the LayoutRulesViewer reflects the new workflow_id.
      queryClient.invalidateQueries({
        queryKey: ["ocr", "layout", "detail", layoutId],
      });
      onApplied?.(selectedWorkflowId);
      onClose();
    },
    onError: (err) =>
      toast.error("Failed to assign workflow", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  const handleApply = () => {
    if (!layoutId) {
      toast.warning("Layout ID is required.");
      return;
    }
    if (!selectedWorkflowId) {
      toast.warning("Select a workflow first.");
      return;
    }
    assignMutation.mutate(selectedWorkflowId);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div
        className="flex h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Workflow className="h-4 w-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-900">
              Assign Workflow
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Selection bar — Layout ID + Workflow ID dropdown + Apply */}
        <div className="flex flex-shrink-0 items-end gap-3 border-b border-gray-200 bg-gray-50/50 px-5 py-3">
          <div className="flex-1">
            <label className="mb-1 block text-[11px] font-medium text-gray-600">
              Layout ID
            </label>
            <input
              type="text"
              value={layoutId}
              readOnly
              className="w-full rounded-md border border-gray-200 bg-gray-100 px-3 py-1.5 text-xs text-gray-700"
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-[11px] font-medium text-gray-600">
              Workflow ID
            </label>
            <select
              value={selectedWorkflowId}
              onChange={(e) => setSelectedWorkflowId(e.target.value)}
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select workflow</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={handleApply}
            disabled={assignMutation.isPending || !selectedWorkflowId}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {assignMutation.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Applying…
              </>
            ) : (
              "Apply Workflow"
            )}
          </button>
        </div>

        {/* Selected workflow preview */}
        {selectedTemplate ? (
          <div className="flex-shrink-0 border-b border-gray-200 px-5 py-3">
            <TemplateCard template={selectedTemplate} isSelected />
          </div>
        ) : null}

        {/* Templates list */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-900">
            Workflow Templates
          </h3>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates…"
              className="w-56 rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          {workflowsQuery.isLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading workflow templates…
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              No templates found.
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTemplates.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  isSelected={t.id === selectedWorkflowId}
                  onSelect={() => setSelectedWorkflowId(t.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateCard({
  template,
  isSelected,
  onSelect,
}: {
  template: WorkflowTemplate;
  isSelected?: boolean;
  onSelect?: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      className={`flex items-center justify-between rounded-lg border bg-white px-4 py-3 transition ${
        isSelected
          ? "border-blue-300 ring-1 ring-blue-200"
          : "border-gray-200 hover:border-gray-300"
      } ${onSelect ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-600">
          <Workflow className="h-4 w-4" />
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-900">{template.id}</div>
          <div className="text-[11px] text-gray-500">Workflow Description</div>
        </div>
      </div>
      <div className="flex flex-1 flex-wrap items-center justify-center gap-1.5 px-4">
        {template.steps.length === 0 ? (
          <span className="text-xs italic text-gray-400">No steps</span>
        ) : (
          template.steps.map((step, i) => (
            <div key={`${step}-${i}`} className="flex items-center gap-1.5">
              <span className="rounded bg-gray-100 px-2 py-1 text-[10px] font-medium tracking-wide text-gray-700">
                {step}
              </span>
              {i < template.steps.length - 1 ? (
                <ChevronRight className="h-3 w-3 text-gray-400" />
              ) : null}
            </div>
          ))
        )}
      </div>
      {isSelected ? (
        <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-blue-600" />
      ) : null}
    </div>
  );
}
